import { Plus } from "lucide-react";
import * as React from "react";
import { Link } from "react-router";

import { useAuth, useNavigation } from "@/providers";
import { PageLayout } from "@/shared/components";
import { TriggerNameLink } from "@/shared/components/links";
import {
  qualifyPrincipal,
  renderPrincipal,
  ResourceView,
  useResourceView,
  type Filter,
  type FilterMatchMode,
} from "@/shared/components/ResourceView";
import { useAgentDirectory } from "@/shared/domain/agents/hooks";
import {
  eventLabel,
  hasActiveFilters,
  isSchedule,
  readableCron,
  TARGET_TYPE_LABELS,
  TriggerTargetType,
  type PaginationState,
  type TriggerFilterParams,
  type TriggerTargetSpec,
  type TriggerV2Record,
} from "@/shared/domain/triggers";
import { useTriggers } from "@/shared/domain/triggers/hooks";
import { Callout } from "@/shared/foundation/primitives";
import { RobotoButton } from "@/shared/foundation/primitives/RobotoButton";
import {
  cellStyles,
  createColumnHelper,
  DataTable,
  FLEX_FILL_COL_DEF,
  renderDashOrValue,
  type ColDef,
  type DataTableLoadingState,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { Goto } from "@/shared/navigation";

import { TriggerOwnerBadge } from "../Trigger/TriggerOwnerBadge";

import styles from "./ListTriggers.module.css";
import { TriggerEnabledSwitch } from "./TriggerEnabledSwitch";
import { TRIGGER_FILTER_DEFINITIONS } from "./triggerFilterDefinitions";

const NO_FILTERS: readonly Filter[] = [];
const NO_TRIGGERS: TriggerV2Record[] = [];

function targetSummary(
  target: TriggerTargetSpec,
  agentNames: ReadonlyMap<string, string>,
): string {
  switch (target.type) {
    case TriggerTargetType.InvokeAction:
      return `${TARGET_TYPE_LABELS[target.type]}: ${target.action.name}`;
    case TriggerTargetType.StartAgent:
      return `${TARGET_TYPE_LABELS[target.type]}: ${
        agentNames.get(target.agent_id) ?? target.agent_id
      }`;
    case TriggerTargetType.SendSlackMessage:
      return TARGET_TYPE_LABELS[target.type];
  }
}

function firesOnSummary(trigger: TriggerV2Record): string {
  return isSchedule(trigger.fires_on)
    ? readableCron(trigger.fires_on.cron)
    : trigger.fires_on.events.map(eventLabel).join(", ");
}

const columnHelper = createColumnHelper<TriggerV2Record>();

/**
 * Why the table is empty. Nothing before the first load succeeds, since
 * whether there are any triggers is then unknown, or when only this page is
 * empty because a refresh removed its triggers.
 */
function NoTriggers({
  paginationState,
  filter,
}: {
  paginationState: PaginationState | undefined;
  filter: TriggerFilterParams;
}) {
  if (paginationState === undefined || paginationState.total > 0) {
    return null;
  }
  return hasActiveFilters(filter) ? (
    <span>No triggers match the current filters.</span>
  ) : (
    <span data-testid="triggers-empty">
      No triggers yet. Click <strong>Create trigger</strong> to get started. To
      control how files are ingested, use{" "}
      <Link to={Goto.route.Settings.Ingestion.Root}>Ingestion rules</Link>.
    </span>
  );
}

/**
 * The `/triggers` page: every trigger in the org, event-fired and
 * schedule-fired alike, as one filterable table. There is one form to create a trigger, so there is one
 * view to see them; "scheduled" is a filter, not a tab.
 *
 * Triggers are deleted from their own page, where it's clear what is being
 * deleted.
 */
export function ListTriggers() {
  const { goto } = useNavigation();

  // A failed toggle is reported in the page's alert, not by its row.
  const [toggleError, setToggleError] = React.useState<Error>();

  const { currentOrganization } = useAuth();
  const agentDirectory = useAgentDirectory(currentOrganization?.org_id);
  const agentNames = React.useMemo(
    () =>
      new Map(
        agentDirectory.agents.map((agent) => [agent.agent_id, agent.name]),
      ),
    [agentDirectory.agents],
  );

  const view = useResourceView<TriggerV2Record>({
    getRowId: (row) => row.trigger_id,
    initialSort: { columnId: "modified", direction: "desc" },
  });
  const structuredFilters =
    view.filters.type === "structured" ? view.filters.filters : NO_FILTERS;
  const matchMode: FilterMatchMode =
    view.filters.type === "structured" ? view.filters.matchMode : "AND";

  const { sort, currentPage, pageSize } = view.table;
  const filter = { filters: structuredFilters, matchMode };
  const triggers = useTriggers({
    sort,
    filter,
    page: currentPage,
    pageSize,
  });
  const { paginationState } = triggers;
  const error = toggleError ?? triggers.error ?? undefined;

  const clearToggleError = React.useCallback(
    () => setToggleError(undefined),
    [],
  );

  // The primitive hides columns unless a visibility picker opts them in; this
  // list has no picker and shows every column, so flip the default.
  const columns = React.useMemo<ColDef<TriggerV2Record>[]>(() => {
    const baseColumns: ColDef<TriggerV2Record>[] = [
      {
        colId: "name",
        headerName: "Name",
        flex: 2,
        sortable: true,
        valueGetter: (p) => p.data?.name ?? "",
        minWidth: 200,
        cellRenderer: (p: ICellRendererParams<TriggerV2Record>) =>
          p.data ? (
            <span className={styles.nameCell}>
              <TriggerNameLink
                triggerName={p.data.name}
                className={cellStyles.primaryContent}
              />
              <TriggerOwnerBadge trigger={p.data} />
            </span>
          ) : null,
      },
      columnHelper.accessor(firesOnSummary, {
        colId: "fires_on",
        headerName: "Fires on",
        flex: 2,
        minWidth: 120,
        sortable: false,
        cellRenderer: renderDashOrValue,
      }),
      columnHelper.accessor(
        (trigger) =>
          trigger.targets
            .map((target) => targetSummary(target, agentNames))
            .join(", "),
        {
          colId: "targets",
          headerName: "Targets",
          flex: 2,
          minWidth: 120,
          sortable: false,
          cellRenderer: renderDashOrValue,
        },
      ),
      columnHelper.timestamp("modified", {
        headerName: "Modified",
        sortable: true,
        variant: "relative",
      }),
      {
        colId: "modified_by",
        headerName: "Modified by",
        minWidth: 150,
        valueGetter: (p) => qualifyPrincipal(p.data?.modified_by),
        cellRenderer: renderPrincipal,
      },
      {
        colId: "enabled",
        headerName: "Enabled",
        flex: 0,
        width: 110,
        cellRenderer: (p: ICellRendererParams<TriggerV2Record>) =>
          p.data ? (
            <TriggerEnabledSwitch
              trigger={p.data}
              onToggle={clearToggleError}
              onError={setToggleError}
            />
          ) : null,
      },
    ];
    return baseColumns.map((column) => ({ hide: false, ...column }));
  }, [agentNames, clearToggleError]);

  // Skeletons only while there's no list to show. Unlike deriveLoadingState, a
  // background refetch keeps the rows on screen.
  const loading: DataTableLoadingState = triggers.isPending
    ? "rowsOnly"
    : "none";

  return (
    <PageLayout title="Roboto - Triggers" headerContentLeft="Triggers">
      <div className={styles.page}>
        <p className={styles.intro} data-testid="triggers-intro">
          Triggers run an action, start an agent, or post to Slack when
          something happens or on a schedule. To control how files are ingested,
          use{" "}
          <Link to={Goto.route.Settings.Ingestion.Root}>Ingestion rules</Link>.
        </p>
        {error !== undefined && (
          <Callout variant="error" className={styles.errorAlert}>
            {error.message}
          </Callout>
        )}
        <ResourceView.Root>
          <ResourceView.Actions>
            <ResourceView.Filters
              filterDefinitions={TRIGGER_FILTER_DEFINITIONS}
              onFilterSelected={view.commit}
            />
            <div className={styles.actionsRight}>
              <RobotoButton
                variant="primary"
                icon={<Plus aria-hidden="true" />}
                onClick={() => goto.createTrigger()}
              >
                Create trigger
              </RobotoButton>
            </div>
          </ResourceView.Actions>
          <ResourceView.ActiveFilters
            filters={view.filters}
            filterDefinitions={TRIGGER_FILTER_DEFINITIONS}
            onRemove={view.removeFilter}
            onUpdate={view.updateFilter}
            onClear={view.clearFilters}
            onMatchModeChange={view.setMatchMode}
            onFilterSelected={view.commit}
          />
          <DataTable<TriggerV2Record>
            table={view.table}
            ariaLabel="Triggers"
            hasNextPage={paginationState?.hasNextPage ?? false}
            loading={loading}
            columns={columns}
            defaultColDef={FLEX_FILL_COL_DEF}
            data={paginationState?.rows ?? NO_TRIGGERS}
            totalRowCount={paginationState?.total}
            noRowsContent={
              <NoTriggers paginationState={paginationState} filter={filter} />
            }
          />
        </ResourceView.Root>
      </div>
    </PageLayout>
  );
}
